/**
 * Starter templates for new custom indicator projects.
 */

import { DEFAULT_INDICATOR_CODE } from "./default-indicator-code";

export interface IndicatorTemplate {
  id: string;
  name: string;
  description: string;
  /** true = overlay on main price chart; false = separate pane */
  overlay: boolean;
  code: string;
}

export const TEMPLATE_TREND_DASHBOARD_ID = "trend-dashboard";

const MACD_PANE_PLOTS = `
    [Plot(Pane = "MACD", Label = "Histogram", Style = "Histogram", BaseValue = 0, Colors = "#26A69A,#EF5350")]
    public Series Hist;

    [PlotColorIndex(Plot = "Hist")]
    public Series HistColor;

    [PlotBarStyle(Plot = "Hist")]
    public Series HistBarStyle;

    [Plot(Pane = "MACD", Label = "MACD", Color = "#2196F3")]
    public Series Macd;

    [Plot(Pane = "MACD", Label = "Signal", Color = "#FF6D00")]
    public Series Signal;

    private double _emaFast;
    private double _emaSlow;
    private double _emaSignal;
    private bool _haveFast;
    private bool _haveSlow;
    private bool _haveSig;

    protected override void OnInit()
    {
        _emaFast = _emaSlow = _emaSignal = 0;
        _haveFast = _haveSlow = _haveSig = false;
    }

    protected override void OnCalculate(int index)
    {
        if (index == PrevCalculated - 1)
        {
            _haveFast = _haveSlow = _haveSig = false;
            _emaFast = _emaSlow = _emaSignal = 0;
        }

        double c = Close[index];
        double kFast = 2.0 / (Fast + 1);
        double kSlow = 2.0 / (Slow + 1);
        double kSig = 2.0 / (SignalPeriod + 1);

        if (!_haveFast)
        {
            _emaFast = c;
            _haveFast = true;
        }
        else
            _emaFast = (c - _emaFast) * kFast + _emaFast;

        if (!_haveSlow)
        {
            _emaSlow = c;
            _haveSlow = true;
        }
        else
            _emaSlow = (c - _emaSlow) * kSlow + _emaSlow;

        if (index < Slow - 1)
        {
            Macd[index] = Signal[index] = Hist[index] = double.NaN;
            HistColor[index] = 0;
            HistBarStyle[index] = 0;
            return;
        }

        double macd = _emaFast - _emaSlow;
        if (!_haveSig)
        {
            _emaSignal = macd;
            _haveSig = true;
            Macd[index] = macd;
            Signal[index] = double.NaN;
            Hist[index] = double.NaN;
            HistColor[index] = 0;
            HistBarStyle[index] = 0;
            return;
        }

        _emaSignal = (macd - _emaSignal) * kSig + _emaSignal;
        Macd[index] = macd;
        Signal[index] = _emaSignal;

        if (index >= Slow + SignalPeriod - 2)
        {
            double h = macd - _emaSignal;
            Hist[index] = h;
            HistColor[index] = h >= 0 ? 0 : 1;
            double prevH = index > 0 ? Hist[index - 1] : double.NaN;
            HistBarStyle[index] = HistogramBarStyleIndex(h, prevH, EnableHollowBodies);
        }
        else
        {
            Hist[index] = double.NaN;
            HistColor[index] = 0;
            HistBarStyle[index] = 0;
        }
    }

    protected override void OnDeinit() { }
`;

function buildMacdPaneTemplateCode(
  className: string,
  indicatorName: string,
  description: string
): string {
  return `using System;
using Vybe.Indicators;

[Pane(Name = "MACD", Height = 160)]
[Indicator(Name = "${indicatorName}", Version = "1.0", Overlay = false, Description = "${description}")]
public class ${className} : Indicator
{
    [Input(Label = "Fast EMA", Min = 2, Max = 100, Step = 1)]
    public int Fast { get; set; } = 12;

    [Input(Label = "Slow EMA", Min = 2, Max = 200, Step = 1)]
    public int Slow { get; set; } = 26;

    [Input(Label = "Signal", Min = 2, Max = 50, Step = 1)]
    public int SignalPeriod { get; set; } = 9;

    [Input(Label = "Hollow histogram bodies", Group = "Histogram")]
    public bool EnableHollowBodies { get; set; } = true;
${MACD_PANE_PLOTS}
}
`;
}

export const INDICATOR_TEMPLATES: IndicatorTemplate[] = [
  {
    id: TEMPLATE_TREND_DASHBOARD_ID,
    name: "Trend Dashboard",
    description:
      "Full showcase: multi-pane MA, MACD, volume, signals, zones, volume profile, dashboard table.",
    overlay: true,
    code: DEFAULT_INDICATOR_CODE,
  },
  {
    id: "minimal-ma",
    name: "Minimal MA",
    description: "Single exponential moving average overlaid on price.",
    overlay: true,
    code: `using System;
using Vybe.Indicators;

[Indicator(Name = "Minimal MA", Version = "1.0", Overlay = true, Description = "Simple EMA overlay")]
public class MinimalMa : Indicator
{
    [Input(Label = "Period", Min = 2, Max = 500, Step = 1)]
    public int Period { get; set; } = 20;

    [Input(Label = "Line color", Group = "Style")]
    public Color LineColor { get; set; } = "#2962FF";

    [Plot(Label = "MA", ColorInput = "LineColor")]
    public Series Ma;

    private double _ema = double.NaN;
    private double _alpha;

    protected override void OnInit()
    {
        _alpha = 2.0 / (Period + 1);
        _ema = double.NaN;
    }

    protected override void OnCalculate(int index)
    {
        if (index < Period - 1)
        {
            Ma[index] = double.NaN;
            return;
        }

        double close = Close[index];
        if (index == Period - 1 || double.IsNaN(_ema))
        {
            double sum = 0;
            for (int i = 0; i < Period; i++)
                sum += Close[index - Period + 1 + i];
            _ema = sum / Period;
        }
        else if (index == PrevCalculated - 1)
        {
            _ema = double.NaN;
            for (int i = Period - 1; i <= index; i++)
            {
                double c = Close[i];
                if (double.IsNaN(_ema))
                {
                    double s = 0;
                    int start = Math.Max(0, i - Period + 1);
                    int count = i - start + 1;
                    for (int j = start; j <= i; j++) s += Close[j];
                    _ema = s / count;
                }
                else
                    _ema = _alpha * c + (1 - _alpha) * _ema;
            }
        }
        else
            _ema = _alpha * close + (1 - _alpha) * _ema;

        Ma[index] = _ema;
    }

    protected override void OnDeinit() { }
}
`,
  },
  {
    id: "rsi-oscillator",
    name: "RSI Oscillator",
    description: "Wilder RSI in a dedicated sub-pane (0–100 scale).",
    overlay: false,
    code: `using System;
using Vybe.Indicators;

[Indicator(Name = "RSI", Version = "1.0", Overlay = false, Description = "Relative Strength Index oscillator")]
public class RsiIndicator : Indicator
{
    [Input(Label = "Period", Min = 2, Max = 200, Step = 1)]
    public int Period { get; set; } = 14;

    [Input(Label = "RSI color", Group = "Style")]
    public Color RsiColor { get; set; } = "#B8860B";

    [Plot(Label = "RSI", ColorInput = "RsiColor")]
    public Series Rsi;

    private double _avgGain;
    private double _avgLoss;

    protected override void OnInit()
    {
        _avgGain = 0;
        _avgLoss = 0;
    }

    protected override void OnCalculate(int index)
    {
        if (index < 1)
        {
            Rsi[index] = double.NaN;
            return;
        }

        double change = Close[index] - Close[index - 1];
        double gain = change > 0 ? change : 0;
        double loss = change < 0 ? -change : 0;

        if (index < Period)
        {
            _avgGain += gain;
            _avgLoss += loss;
            Rsi[index] = double.NaN;
            if (index == Period - 1)
            {
                _avgGain /= Period;
                _avgLoss /= Period;
            }
            return;
        }

        if (index == Period)
        {
            _avgGain = (_avgGain + gain) / Period;
            _avgLoss = (_avgLoss + loss) / Period;
        }
        else
        {
            _avgGain = (_avgGain * (Period - 1) + gain) / Period;
            _avgLoss = (_avgLoss * (Period - 1) + loss) / Period;
        }

        if (_avgLoss < 1e-12)
            Rsi[index] = 100;
        else
        {
            double rs = _avgGain / _avgLoss;
            Rsi[index] = 100 - 100 / (1 + rs);
        }
    }

    protected override void OnDeinit() { }
}
`,
  },
  {
    id: "macd-histogram",
    name: "MACD Histogram",
    description:
      "MACD line, signal, and histogram in a dedicated pane (bars anchored at zero).",
    overlay: false,
    code: buildMacdPaneTemplateCode(
      "MacdTemplate",
      "MACD",
      "MACD with histogram at zero baseline"
    ),
  },
  {
    id: "zero-line-macd",
    name: "Zero-Line MACD",
    description:
      "MACD-style pane: histogram around zero, MACD and signal lines (matches built-in MACD).",
    overlay: false,
    code: buildMacdPaneTemplateCode(
      "ZeroLineMacd",
      "Zero-Line MACD",
      "MACD histogram centered on zero with MACD and signal lines"
    ),
  },
  {
    id: "bollinger-bands",
    name: "Bollinger Bands",
    description: "Upper, middle, and lower bands overlaid on price.",
    overlay: true,
    code: `using System;
using Vybe.Indicators;

[Indicator(Name = "Bollinger Bands", Version = "1.0", Overlay = true, Description = "Bollinger Bands overlay")]
public class BollingerTemplate : Indicator
{
    [Input(Label = "Period", Min = 2, Max = 200, Step = 1)]
    public int Period { get; set; } = 20;

    [Input(Label = "Deviation", Min = 0.1, Max = 5, Step = 0.1)]
    public double Deviation { get; set; } = 2;

    [Plot(Label = "Upper", Color = "#787B86")]
    public Series Upper;

    [Plot(Label = "Middle", Color = "#2962FF")]
    public Series Middle;

    [Plot(Label = "Lower", Color = "#787B86")]
    public Series Lower;

    protected override void OnInit() { }

    protected override void OnCalculate(int index)
    {
        if (index < Period - 1)
        {
            Upper[index] = Middle[index] = Lower[index] = double.NaN;
            return;
        }

        double sum = 0, sumSq = 0;
        for (int i = index - Period + 1; i <= index; i++)
        {
            double v = Close[i];
            sum += v;
            sumSq += v * v;
        }
        double mean = sum / Period;
        double variance = Math.Max(0, sumSq / Period - mean * mean);
        double std = Math.Sqrt(variance);
        Middle[index] = mean;
        Upper[index] = mean + Deviation * std;
        Lower[index] = mean - Deviation * std;
    }

    protected override void OnDeinit() { }
}
`,
  },
  {
    id: "buy-sell-signals",
    name: "Buy / Sell Signals",
    description: "EMA crossover arrows on the main chart.",
    overlay: true,
    code: `using System;
using Vybe.Indicators;

[Indicator(Name = "EMA Cross Signals", Version = "1.0", Overlay = true, Description = "Buy/sell arrows on EMA cross")]
public class EmaCrossSignals : Indicator
{
    [Input(Label = "Fast period", Min = 2, Max = 100, Step = 1)]
    public int FastPeriod { get; set; } = 9;

    [Input(Label = "Slow period", Min = 2, Max = 200, Step = 1)]
    public int SlowPeriod { get; set; } = 21;

    [Plot(Label = "Fast MA", Color = "#2962FF")]
    public Series FastMa;

    [Plot(Label = "Slow MA", Color = "#FF6D00")]
    public Series SlowMa;

    [Signal(
        BuyColor = "#26A69A",
        SellColor = "#EF5350",
        BuyText = "Buy",
        SellText = "Sell",
        ShowLabelsInput = "ShowLabels")]
    public Series SignalState;

    [Input(Label = "Show labels", Group = "Signals")]
    public bool ShowLabels { get; set; } = true;

    private double _fast = double.NaN;
    private double _slow = double.NaN;

    protected override void OnInit()
    {
        _fast = _slow = double.NaN;
    }

    protected override void OnCalculate(int index)
    {
        int start = Math.Max(SlowPeriod - 1, 0);
        if (index < start)
        {
            FastMa[index] = SlowMa[index] = double.NaN;
            SignalState[index] = -1;
            return;
        }

        double af = 2.0 / (FastPeriod + 1);
        double als = 2.0 / (SlowPeriod + 1);
        double c = Close[index];
        _fast = double.IsNaN(_fast) ? c : af * c + (1 - af) * _fast;
        _slow = double.IsNaN(_slow) ? c : als * c + (1 - als) * _slow;
        FastMa[index] = _fast;
        SlowMa[index] = _slow;
        SignalState[index] = -1;

        if (index < 1) return;
        double prevFast = FastMa[index - 1];
        double prevSlow = SlowMa[index - 1];
        if (double.IsNaN(prevFast) || double.IsNaN(prevSlow)) return;

        if (prevFast <= prevSlow && _fast > _slow)
            SignalState[index] = 0;
        else if (prevFast >= prevSlow && _fast < _slow)
            SignalState[index] = 1;
    }

    protected override void OnDeinit() { }
}
`,
  },
];

export function getTemplateById(id: string): IndicatorTemplate | undefined {
  return INDICATOR_TEMPLATES.find((t) => t.id === id);
}

export const DEFAULT_TEMPLATE_ID = TEMPLATE_TREND_DASHBOARD_ID;
